import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { downloadZip } from 'client-zip'

import {
  downloadLinks,
  favourites as loadFavourites,
  openGallery,
  type GalleryPhoto,
  type OpenGallery,
  type OpenResult,
} from './api'

/**
 * The couple's gallery: /g/:slug.
 *
 * Built to match how Jena's Pixieset galleries look (docs/current-tools.md):
 * a full-width cover photo with the title at the bottom left, a light, airy
 * serif, a white page, and a vertical masonry grid in sets that follow the
 * day. Two tabs, Photo and Video (decision 12): films are a large YouTube
 * player with a "Download here" link to Google Drive.
 *
 * Always light, whatever the Studio's theme, and calm: no counters, no
 * badges, nothing that looks like software. The photographs are the page.
 */

const INK = '#1d1b19'
const MUTED = '#8a847c'

export function GalleryPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [result, setResult] = useState<OpenResult | null>(null)

  // The gallery is always the light page, as in Pixieset.
  useEffect(() => {
    const root = document.documentElement
    const previous = root.dataset['theme']
    root.dataset['theme'] = 'light'
    document.body.style.background = '#ffffff'
    return () => {
      if (previous) root.dataset['theme'] = previous
      document.body.style.background = ''
    }
  }, [])

  useEffect(() => {
    void openGallery(slug).then(setResult)
  }, [slug])

  useEffect(() => {
    if (result?.kind === 'open') document.title = `${result.data.gallery.title} · De Leeuw Photo & Film`
  }, [result])

  if (!result) return <Centered>Opening the gallery…</Centered>
  if (result.kind === 'error') return <Centered>{result.message}</Centered>
  if (result.kind === 'password') {
    return <PasswordScreen title={result.title} error={result.error} onSubmit={(password) => void openGallery(slug, password).then(setResult)} />
  }
  return <Gallery data={result.data} />
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-white px-6 text-center font-[family-name:var(--font-body)] text-[15px] font-light" style={{ color: MUTED }}>
      {children}
    </div>
  )
}

function PasswordScreen({ title, error, onSubmit }: { title: string | null; error: string | null; onSubmit: (password: string) => void }) {
  const [password, setPassword] = useState('')
  return (
    <div className="grid min-h-dvh place-items-center bg-white px-6" style={{ color: INK }}>
      <form
        className="flex w-full max-w-sm flex-col items-center gap-6 text-center"
        onSubmit={(event) => {
          event.preventDefault()
          if (password.trim()) onSubmit(password)
        }}
      >
        <p className="font-[family-name:var(--font-body)] text-[11px] font-light tracking-[0.3em]" style={{ color: MUTED }}>
          De Leeuw Photo &amp; Film
        </p>
        <h1 className="font-[family-name:var(--font-display)] text-[40px] font-light leading-tight">{title ?? 'Your gallery'}</h1>
        <input
          type="password"
          autoFocus
          aria-label="Gallery password"
          placeholder="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="w-full border-b bg-transparent py-2 text-center font-[family-name:var(--font-body)] text-[15px] font-light outline-none"
          style={{ borderColor: '#d8d5d1' }}
        />
        {error ? <p className="text-[13px]" style={{ color: '#a0503a' }}>{error}</p> : null}
        <button type="submit" className="border px-8 py-3 font-[family-name:var(--font-body)] text-[11px]" style={{ borderColor: INK }}>
          Open the gallery
        </button>
      </form>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The open gallery
// ---------------------------------------------------------------------------

function Gallery({ data }: { data: OpenGallery }) {
  const { gallery, sets, photos, films, token } = data
  const [tab, setTab] = useState<'photo' | 'video'>('photo')
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [hearts, setHearts] = useState<Set<string>>(new Set())
  const [onlyHearts, setOnlyHearts] = useState(false)
  const [pinFor, setPinFor] = useState<null | { what: { photoId?: string; setId?: string; all?: boolean }; label: string }>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const pin = useRef<string | undefined>(undefined)
  const gridTop = useRef<HTMLDivElement>(null)

  useEffect(() => {
    void loadFavourites(token).then((ids) => setHearts(new Set(ids)))
  }, [token])

  const visible = useMemo(() => (onlyHearts ? photos.filter((p) => hearts.has(p.id)) : photos), [photos, hearts, onlyHearts])
  const cover = photos.find((p) => p.id === gallery.coverPhotoId) ?? photos[0]

  const toggleHeart = useCallback(
    (photoId: string) => {
      const on = !hearts.has(photoId)
      setHearts((current) => {
        const next = new Set(current)
        if (on) next.add(photoId)
        else next.delete(photoId)
        return next
      })
      void loadFavourites(token, photoId, on)
    },
    [hearts, token],
  )

  async function download(what: { photoId?: string; setId?: string; all?: boolean }, label: string) {
    setBusy(label)
    try {
      const result = await downloadLinks(token, what, pin.current)
      if (result.kind === 'pin') {
        setPinFor({ what, label })
        return
      }
      if (result.kind === 'error') {
        window.alert(result.message)
        return
      }
      if (result.files.length === 1) {
        const file = result.files[0]
        if (file) triggerDownload(file.url, file.name)
        return
      }
      // Several photos: zipped in the browser, streamed from the signed links.
      const zip = downloadZip(
        (async function* () {
          for (const file of result.files) {
            const response = await fetch(file.url)
            yield { name: file.name, input: response }
          }
        })(),
      )
      const blob = await zip.blob()
      const url = URL.createObjectURL(blob)
      triggerDownload(url, `${gallery.title} - ${label}.zip`.replace(/[\\/:*?"<>|]/g, ''))
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="min-h-dvh bg-white font-[family-name:var(--font-body)]" style={{ color: INK }}>
      {gallery.draft ? (
        <div className="bg-[#1d1b19] py-1.5 text-center text-[11px] text-white">Preview: not delivered yet</div>
      ) : null}

      {/* ── Cover ─────────────────────────────────────────────────────── */}
      <header className="relative h-[100svh] min-h-[480px] w-full overflow-hidden">
        {cover?.display ? (
          <img
            src={cover.display}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            style={{ backgroundImage: cover.lqip ? `url(${cover.lqip})` : undefined, backgroundSize: 'cover' }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
        <div className="absolute bottom-0 left-0 p-8 text-white sm:p-14">
          <p className="mb-3 text-[11px] font-light tracking-[0.3em] opacity-90">De Leeuw Photo &amp; Film</p>
          <h1 className="font-[family-name:var(--font-display)] text-[44px] font-light leading-[1.05] sm:text-[64px]">{gallery.title}</h1>
          {gallery.subtitle ? <p className="mt-3 text-[13px] font-light tracking-[0.12em] opacity-90">{gallery.subtitle}</p> : null}
          <button
            type="button"
            onClick={() => gridTop.current?.scrollIntoView({ behavior: 'smooth' })}
            className="mt-8 border border-white/80 px-7 py-3 text-[11px] transition-colors hover:bg-white hover:text-[#1d1b19]"
          >
            View gallery
          </button>
        </div>
      </header>

      {/* ── Bar ───────────────────────────────────────────────────────── */}
      <div ref={gridTop} className="sticky top-0 z-20 border-b bg-white/95 backdrop-blur" style={{ borderColor: '#eeebe7' }}>
        <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 px-4 py-3 sm:px-8">
          <p className="hidden min-w-0 truncate font-[family-name:var(--font-display)] text-[20px] font-light sm:block">{gallery.title}</p>
          <nav className="flex gap-6 text-[11px]">
            <TabButton active={tab === 'photo'} onClick={() => setTab('photo')}>Photo</TabButton>
            {films.length > 0 ? <TabButton active={tab === 'video'} onClick={() => setTab('video')}>Video</TabButton> : null}
          </nav>
          {tab === 'photo' ? (
            <div className="flex items-center gap-4 text-[11px]">
              <button type="button" onClick={() => setOnlyHearts((v) => !v)} className="flex items-center gap-1.5" aria-pressed={onlyHearts}>
                <Heart filled={onlyHearts || hearts.size > 0} />
                <span className="hidden sm:inline">{onlyHearts ? 'All photos' : `Favourites${hearts.size ? ` (${hearts.size})` : ''}`}</span>
              </button>
              {gallery.downloadsEnabled ? (
                <button type="button" disabled={busy !== null} onClick={() => void download({ all: true }, 'All photos')} className="flex items-center gap-1.5 disabled:opacity-50">
                  <DownloadIcon />
                  <span className="hidden sm:inline">{busy === 'All photos' ? 'Preparing…' : 'Download all'}</span>
                </button>
              ) : null}
            </div>
          ) : (
            <span className="w-24" />
          )}
        </div>
        {tab === 'photo' && !onlyHearts && sets.length > 1 ? (
          <div className="mx-auto flex max-w-[1600px] gap-6 overflow-x-auto px-4 pb-3 text-[11px] sm:px-8" style={{ color: MUTED }}>
            {sets.map((set) => (
              <a key={set.id} href={`#set-${set.id}`} className="shrink-0 hover:text-[#1d1b19]">
                {set.title}
              </a>
            ))}
          </div>
        ) : null}
      </div>

      {/* ── Photo ─────────────────────────────────────────────────────── */}
      {tab === 'photo' ? (
        <main className="mx-auto max-w-[1600px] px-2 pb-24 pt-6 sm:px-6">
          {onlyHearts ? (
            visible.length === 0 ? (
              <p className="py-24 text-center text-[14px] font-light" style={{ color: MUTED }}>
                Tap the heart on any photo to keep it here.
              </p>
            ) : (
              <Masonry photos={visible} onOpen={(id) => setLightbox(visible.findIndex((p) => p.id === id))} hearts={hearts} onHeart={toggleHeart} />
            )
          ) : (
            sets.map((set) => {
              const inSet = photos.filter((p) => p.setId === set.id)
              if (inSet.length === 0) return null
              return (
                <section key={set.id} id={`set-${set.id}`} className="scroll-mt-28 pb-14">
                  <div className="flex items-baseline justify-between px-2 pb-5 pt-8">
                    <h2 className="font-[family-name:var(--font-display)] text-[28px] font-light">{set.title}</h2>
                    {gallery.downloadsEnabled ? (
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => void download({ setId: set.id }, set.title)}
                        className="text-[11px] disabled:opacity-50"
                        style={{ color: MUTED }}
                      >
                        {busy === set.title ? 'Preparing…' : 'Download set'}
                      </button>
                    ) : null}
                  </div>
                  <Masonry photos={inSet} onOpen={(id) => setLightbox(visible.findIndex((p) => p.id === id))} hearts={hearts} onHeart={toggleHeart} />
                </section>
              )
            })
          )}
        </main>
      ) : (
        <Films films={films} />
      )}

      <footer className="pb-12 text-center text-[11px] font-light tracking-[0.3em]" style={{ color: MUTED }}>
        De Leeuw Photo &amp; Film
      </footer>

      {lightbox !== null && visible[lightbox] ? (
        <Lightbox
          photos={visible}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
          hearts={hearts}
          onHeart={toggleHeart}
          onDownload={gallery.downloadsEnabled ? (id) => void download({ photoId: id }, 'photo') : null}
        />
      ) : null}

      {pinFor ? (
        <PinDialog
          onCancel={() => setPinFor(null)}
          onSubmit={(value) => {
            pin.current = value
            const request = pinFor
            setPinFor(null)
            void download(request.what, request.label)
          }}
        />
      ) : null}
    </div>
  )
}

function triggerDownload(url: string, name: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="border-b pb-0.5 transition-colors" style={{ borderColor: active ? INK : 'transparent', color: active ? INK : MUTED }}>
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Masonry: vertical columns, each photo placed in the shortest column so the
// order still reads left to right, top to bottom.
// ---------------------------------------------------------------------------

function useColumns(): number {
  const [columns, setColumns] = useState(() => pick(typeof window === 'undefined' ? 1200 : window.innerWidth))
  useEffect(() => {
    const onResize = () => setColumns(pick(window.innerWidth))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return columns
}
const pick = (width: number) => (width < 640 ? 2 : width < 1100 ? 3 : 4)

function Masonry({ photos, onOpen, hearts, onHeart }: { photos: GalleryPhoto[]; onOpen: (id: string) => void; hearts: Set<string>; onHeart: (id: string) => void }) {
  const count = useColumns()
  const columns = useMemo(() => {
    const cols: GalleryPhoto[][] = Array.from({ length: count }, () => [])
    const heights = new Array<number>(count).fill(0)
    for (const photo of photos) {
      const shortest = heights.indexOf(Math.min(...heights))
      cols[shortest]?.push(photo)
      heights[shortest] = (heights[shortest] ?? 0) + photo.height / photo.width
    }
    return cols
  }, [photos, count])

  return (
    <div className="flex gap-2 sm:gap-3">
      {columns.map((col, i) => (
        <div key={i} className="flex min-w-0 flex-1 flex-col gap-2 sm:gap-3">
          {col.map((photo) => (
            <figure key={photo.id} className="group relative overflow-hidden" style={{ aspectRatio: `${photo.width} / ${photo.height}` }}>
              <button type="button" onClick={() => onOpen(photo.id)} className="block h-full w-full" aria-label="Open photo">
                <img
                  src={photo.thumb ?? undefined}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width={photo.width}
                  height={photo.height}
                  className="h-full w-full object-cover"
                  style={{ backgroundImage: photo.lqip ? `url(${photo.lqip})` : undefined, backgroundSize: 'cover' }}
                />
              </button>
              <button
                type="button"
                onClick={() => onHeart(photo.id)}
                aria-label={hearts.has(photo.id) ? 'Remove from favourites' : 'Add to favourites'}
                className={`absolute bottom-2 right-2 grid size-8 place-items-center text-white transition-opacity ${hearts.has(photo.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
                style={{ filter: 'drop-shadow(0 1px 2px rgb(0 0 0 / 0.5))' }}
              >
                <Heart filled={hearts.has(photo.id)} />
              </button>
            </figure>
          ))}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lightbox: white, quiet, swipe and arrow keys, neighbours preloaded.
// ---------------------------------------------------------------------------

function Lightbox({
  photos,
  index,
  onIndex,
  onClose,
  hearts,
  onHeart,
  onDownload,
}: {
  photos: GalleryPhoto[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
  hearts: Set<string>
  onHeart: (id: string) => void
  onDownload: ((id: string) => void) | null
}) {
  const photo = photos[index]
  const touch = useRef<number | null>(null)
  const go = useCallback((delta: number) => onIndex((index + delta + photos.length) % photos.length), [index, photos.length, onIndex])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') go(1)
      if (event.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [go, onClose])

  // Decode the neighbours so the next swipe is instant.
  useEffect(() => {
    for (const delta of [1, -1, 2]) {
      const next = photos[(index + delta + photos.length) % photos.length]
      if (next?.display) {
        const img = new Image()
        img.src = next.display
      }
    }
  }, [index, photos])

  if (!photo) return null

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      onTouchStart={(event) => (touch.current = event.touches[0]?.clientX ?? null)}
      onTouchEnd={(event) => {
        const start = touch.current
        const end = event.changedTouches[0]?.clientX
        if (start !== null && end !== undefined && Math.abs(end - start) > 50) go(end < start ? 1 : -1)
        touch.current = null
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 text-[11px]" style={{ color: MUTED }}>
        <span>
          {index + 1} / {photos.length}
        </span>
        <div className="flex items-center gap-5" style={{ color: INK }}>
          <button type="button" onClick={() => onHeart(photo.id)} aria-label="Favourite">
            <Heart filled={hearts.has(photo.id)} />
          </button>
          {onDownload ? (
            <button type="button" onClick={() => onDownload(photo.id)} aria-label="Download this photo">
              <DownloadIcon />
            </button>
          ) : null}
          <button type="button" onClick={onClose} aria-label="Close" className="text-[20px] leading-none">
            ×
          </button>
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-4 sm:px-16">
        <img
          key={photo.id}
          src={photo.display ?? undefined}
          alt=""
          className="max-h-full max-w-full object-contain"
          style={{ backgroundImage: photo.lqip ? `url(${photo.lqip})` : undefined, backgroundSize: 'cover' }}
        />
        <button type="button" onClick={() => go(-1)} aria-label="Previous" className="absolute left-0 top-0 hidden h-full w-16 items-center justify-center text-[28px] font-light sm:flex" style={{ color: MUTED }}>
          ‹
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next" className="absolute right-0 top-0 hidden h-full w-16 items-center justify-center text-[28px] font-light sm:flex" style={{ color: MUTED }}>
          ›
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Video: a large YouTube player (loads on click) and the Drive download.
// ---------------------------------------------------------------------------

function Films({ films }: { films: OpenGallery['films'] }) {
  return (
    <main className="mx-auto flex max-w-[1280px] flex-col gap-20 px-4 pb-24 pt-12 sm:px-8">
      {films.map((film) => (
        <section key={film.id} className="flex flex-col items-center gap-6">
          <h2 className="font-[family-name:var(--font-display)] text-[32px] font-light">{film.title}</h2>
          {film.youtube_id ? <YouTube id={film.youtube_id} title={film.title} /> : null}
          {film.drive_url ? (
            <a
              href={film.drive_url}
              target="_blank"
              rel="noreferrer noopener"
              className="border px-8 py-3 text-[11px] transition-colors hover:bg-[#1d1b19] hover:text-white"
              style={{ borderColor: INK }}
            >
              Download here
            </a>
          ) : null}
        </section>
      ))}
    </main>
  )
}

function YouTube({ id, title }: { id: string; title: string }) {
  const [playing, setPlaying] = useState(false)
  return (
    <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0" aria-label={`Play ${title}`}>
          <img src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`} alt="" className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100" />
          <span className="absolute left-1/2 top-1/2 grid size-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/90 text-white">
            <svg width="22" height="24" viewBox="0 0 22 24" aria-hidden>
              <path d="M2 2l18 10L2 22z" fill="currentColor" />
            </svg>
          </span>
        </button>
      )}
    </div>
  )
}

function PinDialog({ onSubmit, onCancel }: { onSubmit: (pin: string) => void; onCancel: () => void }) {
  const [pin, setPin] = useState('')
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-white/90 px-6" role="dialog" aria-modal="true">
      <form
        className="flex w-full max-w-xs flex-col items-center gap-5 text-center"
        onSubmit={(event) => {
          event.preventDefault()
          if (pin.trim()) onSubmit(pin.trim())
        }}
      >
        <p className="font-[family-name:var(--font-display)] text-[28px] font-light">Download PIN</p>
        <p className="text-[13px] font-light" style={{ color: MUTED }}>
          Enter the 4-digit PIN from your gallery email.
        </p>
        <input
          autoFocus
          inputMode="numeric"
          maxLength={8}
          aria-label="Download PIN"
          value={pin}
          onChange={(event) => setPin(event.target.value)}
          className="w-32 border-b bg-transparent py-2 text-center text-[20px] tracking-[0.4em] outline-none"
          style={{ borderColor: '#d8d5d1' }}
        />
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="px-5 py-2.5 text-[11px]" style={{ color: MUTED }}>
            Cancel
          </button>
          <button type="submit" className="border px-6 py-2.5 text-[11px]" style={{ borderColor: INK }}>
            Download
          </button>
        </div>
      </form>
    </div>
  )
}

function Heart({ filled }: { filled: boolean }) {
  return (
    <svg width="16" height="15" viewBox="0 0 16 15" aria-hidden>
      <path
        d="M8 14S1 9.6 1 4.9A3.6 3.6 0 0 1 8 3.4a3.6 3.6 0 0 1 7 1.5C15 9.6 8 14 8 14Z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg width="14" height="15" viewBox="0 0 14 15" aria-hidden>
      <path d="M7 1v9M3 6.5 7 10l4-3.5M1 14h12" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}
